import { Info } from "lucide-react";
import { cn } from "@/utils/cn";

interface InfoTooltipProps {
  text: string;
  /** Extra class applied to the tooltip bubble — use "nightguest-tooltip-content" for the night-guest variant */
  contentClassName?: string;
  /**
   * Where the bubble appears relative to the trigger.
   * "top" (default) — above; use "bottom" inside sticky/overflow-hidden containers
   * where upward tooltips get clipped (e.g. table column headers).
   */
  placement?: "top" | "bottom";
  /** Custom trigger element. When omitted the default ℹ Info icon is shown. */
  children?: React.ReactNode;
}

export function InfoTooltip({ text, contentClassName, placement = "top", children }: InfoTooltipProps) {
  const positionCls = placement === "bottom" ? "top-full mt-1" : "bottom-full mb-1";

  return (
    <span className="group relative inline-flex shrink-0 items-center">
      {children ?? <Info className="h-3 w-3 cursor-help text-slate-400" aria-label={text} />}
      <span
        role="tooltip"
        className={cn(
          "pointer-events-none absolute left-1/2 z-50 w-60 -translate-x-1/2",
          positionCls,
          "bg-black px-3 py-2 text-white shadow-none",
          "custom-tooltip-content",
          "invisible opacity-0 transition-opacity group-hover:visible group-hover:opacity-100",
          contentClassName
        )}
        style={{ borderRadius: 0 }}
      >
        {text.split("\n").map((line, i) => (
          <div key={i}>{line}</div>
        ))}
      </span>
    </span>
  );
}
